import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, setDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { convertObjectDates } from '../../../../utils/firebase-timestamp-transformation';
import './BankInfoTab.scss';

const BankInfoTab = () => {
  const [bankInfoList, setBankInfoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [migrating, setMigrating] = useState(null);

  useEffect(() => {
    fetchBankInfo();
  }, []);

  const fetchBankInfo = async () => {
    try {
      setLoading(true);
      const bankInfoRef = collection(db, 'bankInfo');
      const querySnapshot = await getDocs(bankInfoRef);
      
      const bankInfos = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setBankInfoList(bankInfos);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bank info:', error);
      setLoading(false);
    }
  };

  // Function to find new email for an employee from emailChanges.js
  const findNewEmail = (oldEmail) => {
    const change = emailChanges.find(change => change.oldEmail === oldEmail);
    return change ? change.newEmail : null;
  };

  // Handle migrate button click
  const handleMigrate = async (bankInfo) => {
    const oldEmail = bankInfo.id; // Assuming id is the email
    const newEmail = findNewEmail(oldEmail);
    
    if (!newEmail) {
      alert(`No migration mapping found for ${oldEmail} in emailChanges.js`);
      return;
    }
    
    if (!window.confirm(`Are you sure you want to migrate bank info for ${oldEmail} to ${newEmail}?`)) {
      return;
    }
    
    try {
      setMigrating(oldEmail);
      
      // Check if bank info with the new email already exists
      const bankInfoRef = collection(db, 'bankInfo');
      const newEmailQuery = query(bankInfoRef, where('id', '==', newEmail));
      const newEmailSnapshot = await getDocs(newEmailQuery);
      
      if (!newEmailSnapshot.empty) {
        alert(`Bank info for ${newEmail} already exists in the database.`);
        setMigrating(null);
        return;
      }
      
      // Convert any timestamp fields to proper JavaScript Date objects
      const processedBankInfo = convertObjectDates(bankInfo, ['lastUpdated']);
      
      // Create new document with updated email
      const newBankInfoData = {
        ...processedBankInfo,
        id: newEmail,
        oldEmail: oldEmail,
        migrated: true,
        migratedAt: new Date()
      };
      
      // Use the new email as the document ID
      const newBankInfoRef = doc(db, 'bankInfo', newEmail);
      await setDoc(newBankInfoRef, newBankInfoData);
      
      alert(`Successfully migrated bank info from ${oldEmail} to ${newEmail}`);
      
      // Refresh the list
      fetchBankInfo();
      
    } catch (error) {
      console.error('Error migrating bank info:', error);
      alert(`Error migrating bank info: ${error.message}`);
    } finally {
      setMigrating(null);
    }
  };

  const filteredBankInfo = searchTerm 
    ? bankInfoList.filter(info => 
        info.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        info.accountName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        info.bankName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        info.accountNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : bankInfoList;

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // If it's a timestamp (has seconds property)
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString();
    }
    
    // If it's already a Date object
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    
    // Try to parse as date string
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="bank-info-tab">
      <div className="bank-info-header">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by email, account name or bank..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="refresh-container">
          <button 
            className="refresh-button"
            onClick={fetchBankInfo}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading bank information...</p>
        </div>
      ) : filteredBankInfo.length > 0 ? (
        <div className="bank-info-table-container">
          <table className="bank-info-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Account Name</th>
                <th>Bank Name</th>
                <th>Account Number</th>
                <th>Branch</th>
                <th>Country</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBankInfo.map(info => (
                <tr key={info.id} className={info.migrated ? 'migrated-row' : ''}>
                  <td>
                    {info.id}
                    {info.migrated && <span className="migrated-badge">Migrated</span>}
                  </td>
                  <td>{info.accountName || 'N/A'}</td>
                  <td>{info.bankName || 'N/A'}</td>
                  <td>{info.accountNumber || 'N/A'}</td>
                  <td>{info.branch || 'N/A'}</td>
                  <td>{info.country || 'N/A'}</td>
                  <td>{formatDate(info.lastUpdated)}</td>
                  <td>
                    {!info.migrated && findNewEmail(info.id) && (
                      <button
                        className="migrate-button"
                        onClick={() => handleMigrate(info)}
                        disabled={migrating === info.id}
                      >
                        {migrating === info.id ? 'Migrating...' : 'Migrate'}
                      </button>
                    )}
                    {info.migrated && (
                      <span className="already-migrated">Already Migrated</span>
                    )}
                    {!findNewEmail(info.id) && !info.migrated && (
                      <span className="no-mapping">No Mapping</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          <p>No bank information found.</p>
        </div>
      )}
    </div>
  );
};

export default BankInfoTab; 